import { useState } from "react";
import {
  CreditCard,
  TrendingUp,
  TrendingDown,
  Eye,
  EyeOff,
  ArrowLeftRight,
  Landmark,
  ShoppingBag,
  Banknote,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  LogIn,
  Wallet,
  Activity,
  CircleDot,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const ITEMS_PER_PAGE = 10;

/* ─── ICONE TRANSAZIONE ─── */
function TransactionIcon({ tipo }) {
  const iconMap = {
    deposito: { icon: Landmark, color: "text-sky-300", bg: "bg-sky-500/10", border: "border-sky-500/25" },
    stipendio: { icon: Wallet, color: "text-violet-300", bg: "bg-violet-500/10", border: "border-violet-500/25" },
    acquisto: { icon: ShoppingBag, color: "text-rose-300", bg: "bg-rose-500/10", border: "border-rose-500/25" },
    trasferimento: { icon: ArrowLeftRight, color: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/25" },
  };
  const config = iconMap[tipo] || iconMap.deposito;
  const Icon = config.icon;
  return (
    <span className={`shrink-0 inline-flex w-9 h-9 rounded-xl items-center justify-center ${config.bg} ${config.color} border ${config.border}`}>
      <Icon className="w-4 h-4" />
    </span>
  );
}

/* ─── CUSTOM TOOLTIP GRAFICO ─── */
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl bg-[hsl(222_20%_11%)] border border-[hsl(222_18%_20%)] p-3.5 shadow-2xl min-w-[140px]">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-[hsl(220_14%_60%)] mb-2">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center justify-between gap-4">
          <span className="text-xs text-[hsl(220_14%_70%)]">{entry.name}</span>
          <span className="text-sm font-bold" style={{ color: entry.color }}>
            {Number(entry.value).toLocaleString("it-IT")} €
          </span>
        </div>
      ))}
    </div>
  );
}

/* ─── PLACEHOLDER ─── */
function NotAvailable({ text = "—" }) {
  return <span className="text-[hsl(var(--text-mute))] italic">{text}</span>;
}

/* ─── STATO NON LOGGATO ─── */
function NotLoggedIn({ onLogin }) {
  return (
    <div className="mt-12 relative overflow-hidden rounded-3xl border border-[hsl(var(--border-soft))] bg-gradient-to-br from-[hsl(var(--bg-2)/0.9)] to-[hsl(var(--bg-1)/0.95)] p-12 text-center">
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="h-64 w-64 rounded-full bg-emerald-500/6 blur-3xl" />
      </div>
      <div className="relative">
        <span className="inline-flex w-20 h-20 rounded-2xl items-center justify-center bg-gradient-to-br from-emerald-400/20 to-emerald-600/10 border border-emerald-500/25 mb-6">
          <CreditCard className="w-9 h-9 text-emerald-400" strokeWidth={1.4} />
        </span>
        <p className="text-2xl font-semibold text-white">Accesso richiesto</p>
        <p className="text-sm mt-3 text-[hsl(var(--text-soft))] max-w-sm mx-auto leading-relaxed">
          Effettua il login per visualizzare il tuo conto corrente, i movimenti e l'andamento finanziario.
        </p>
        {onLogin && (
          <button onClick={onLogin} className="btn-brand inline-flex items-center gap-2 mt-7">
            <LogIn className="w-4 h-4" /> Accedi adesso
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── COMPONENTE PRINCIPALE ─── */
export default function ContoCorrente({ stats, user, onLogin }) {
  const [showPin, setShowPin] = useState(false);
  const [page, setPage] = useState(1);

  const isLogged = !!user;

  const transactions = stats?.transazioni || [];
  const chartData = stats?.andamento || [];
  const carta = stats?.carta || null;
  const entrate = stats?.entrateSettimanali;
  const uscite = stats?.usciteSettimanali;

  const totalPages = Math.max(1, Math.ceil(transactions.length / ITEMS_PER_PAGE));
  const paginatedTransactions = transactions.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  return (
    <section className="mt-20" data-testid="section-conto">

      {/* ── Header ── */}
      <div className="text-center fade-up">
        <h2 className="font-display title-stroke text-5xl sm:text-7xl tracking-wider leading-none">
          Conto Corrente
        </h2>
        <p className="mt-4 text-[10px] sm:text-xs uppercase tracking-[0.45em] text-[hsl(var(--text-mute))]">
          Gestisci le tue finanze e monitora le transazioni
        </p>
        <div className="conto-divider mt-7" />
      </div>

      {!isLogged && <NotLoggedIn onLogin={onLogin} />}

      {isLogged && (
        <div className="mt-12 space-y-6">

          {/* ══════════════════════════════════════
              RIGA 1 — Carta + KPI settimanali
          ══════════════════════════════════════ */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Carta — occupa 2 colonne */}
            <div
              className="lg:col-span-2 relative overflow-hidden rounded-3xl border border-[hsl(var(--border-soft))] bg-gradient-to-br from-[hsl(var(--bg-2)/0.9)] via-[hsl(var(--bg-2)/0.7)] to-[hsl(var(--bg-1)/0.95)] p-6"
              data-testid="carta-card"
            >
              {/* Glow decorativo in alto a destra */}
              <div className="pointer-events-none absolute -top-10 -right-10 w-48 h-48 rounded-full bg-emerald-500/8 blur-3xl" />

              <div className="relative flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <span className="inline-flex w-13 h-13 w-12 h-12 rounded-2xl items-center justify-center bg-gradient-to-br from-emerald-400/20 to-emerald-600/10 border border-emerald-500/30">
                    <CreditCard className="w-6 h-6 text-emerald-300" />
                  </span>
                  <div>
                    <h3 className="text-lg font-semibold text-white leading-tight">La tua carta</h3>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--text-mute))] mt-1">
                      {carta?.tipo || <NotAvailable text="Tipo non disponibile" />}
                    </p>
                  </div>
                </div>

                {carta ? (
                  <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold tracking-wider px-3 py-1.5 rounded-xl ${carta.attiva ? "bg-emerald-500/12 text-emerald-300 border border-emerald-500/30" : "bg-red-500/12 text-red-300 border border-red-500/30"}`}>
                    <ShieldCheck className="w-3.5 h-3.5" />
                    {carta.attiva ? "ATTIVA" : "DISATTIVA"}
                  </span>
                ) : (
                  <span className="text-xs text-[hsl(var(--text-mute))]">—</span>
                )}
              </div>

              {/* Sub-tiles */}
              <div className="relative grid grid-cols-3 gap-3 mt-6">
                <div className="sub-tile">
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-mute))]">Pin carta</div>
                  <div className="text-sm font-semibold text-white mt-1.5 flex items-center gap-2">
                    {carta?.pin ? (
                      <>
                        <span className="font-mono tracking-widest">{showPin ? carta.pin : "••••"}</span>
                        <button
                          onClick={() => setShowPin(!showPin)}
                          className="text-[hsl(var(--text-mute))] hover:text-white transition"
                        >
                          {showPin ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                      </>
                    ) : (
                      <NotAvailable />
                    )}
                  </div>
                </div>
                <div className="sub-tile">
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-mute))]">Costo sett.</div>
                  <div className="text-sm font-semibold text-white mt-1.5">
                    {carta?.costoSettimanale != null
                      ? `${carta.costoSettimanale.toLocaleString("it-IT", { minimumFractionDigits: 2 })} €`
                      : <NotAvailable />}
                  </div>
                </div>
                <div className="sub-tile">
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-mute))]">Limite conto</div>
                  <div className="text-sm font-semibold text-white mt-1.5">
                    {carta?.limiteConto != null
                      ? `${carta.limiteConto.toLocaleString("it-IT", { minimumFractionDigits: 2 })} €`
                      : <NotAvailable />}
                  </div>
                </div>
              </div>
            </div>

            {/* KPI settimanali — colonna destra */}
            <div className="flex flex-col gap-4">
              {/* Entrate */}
              <div className="flex-1 relative overflow-hidden rounded-3xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/8 to-[hsl(var(--bg-1)/0.95)] p-5">
                <div className="pointer-events-none absolute -bottom-6 -right-6 w-28 h-28 rounded-full bg-emerald-500/12 blur-2xl" />
                <div className="relative">
                  <div className="flex items-center gap-2 text-emerald-400 text-xs font-semibold uppercase tracking-wider mb-3">
                    <TrendingUp className="w-4 h-4" />
                    Entrate sett.
                  </div>
                  <div className="text-3xl font-bold text-emerald-400 leading-none">
                    {entrate != null
                      ? `+${Number(entrate).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`
                      : <NotAvailable />}
                  </div>
                  {entrate != null && <div className="text-sm text-emerald-400/60 mt-1">€</div>}
                </div>
              </div>

              {/* Uscite */}
              <div className="flex-1 relative overflow-hidden rounded-3xl border border-red-500/20 bg-gradient-to-br from-red-500/8 to-[hsl(var(--bg-1)/0.95)] p-5">
                <div className="pointer-events-none absolute -bottom-6 -right-6 w-28 h-28 rounded-full bg-red-500/12 blur-2xl" />
                <div className="relative">
                  <div className="flex items-center gap-2 text-red-400 text-xs font-semibold uppercase tracking-wider mb-3">
                    <TrendingDown className="w-4 h-4" />
                    Uscite sett.
                  </div>
                  <div className="text-3xl font-bold text-red-400 leading-none">
                    {uscite != null
                      ? `-${Number(uscite).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`
                      : <NotAvailable />}
                  </div>
                  {uscite != null && <div className="text-sm text-red-400/60 mt-1">€</div>}
                </div>
              </div>
            </div>
          </div>

          {/* ══════════════════════════════════════
              RIGA 2 — Grafico + Movimenti
          ══════════════════════════════════════ */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

            {/* Grafico Andamento — 3 colonne */}
            <div
              className="lg:col-span-3 rounded-3xl border border-[hsl(var(--border-soft))] bg-gradient-to-b from-[hsl(var(--bg-2)/0.85)] to-[hsl(var(--bg-1)/0.95)] p-6"
              data-testid="chart-card"
            >
              <div className="flex items-center gap-4 mb-6">
                <span className="inline-flex w-11 h-11 rounded-2xl items-center justify-center bg-sky-500/12 border border-sky-500/25">
                  <Activity className="w-5 h-5 text-sky-300" />
                </span>
                <div>
                  <h3 className="text-lg font-semibold text-white leading-tight">Andamento finanziario</h3>
                  <p className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--text-mute))] mt-1">
                    Entrate ed uscite mensili
                  </p>
                </div>
              </div>

              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={chartData} margin={{ top: 5, right: 8, left: 4, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradEntrate" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#34d399" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#34d399" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradUscite" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f87171" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#f87171" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 18% 17%)" vertical={false} />
                    <XAxis
                      dataKey="mese"
                      tick={{ fill: "hsl(220 14% 60%)", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: "hsl(220 14% 60%)", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => `€${v.toLocaleString("it-IT")}`}
                      width={70}
                    />
                    <Tooltip content={<ChartTooltip />} cursor={{ stroke: "hsl(222 18% 28%)", strokeWidth: 1 }} />
                    <Area
                      type="monotone"
                      dataKey="entrate"
                      name="Entrate"
                      stroke="#34d399"
                      strokeWidth={2}
                      fill="url(#gradEntrate)"
                      dot={false}
                      activeDot={{ r: 4, fill: "#34d399", stroke: "#0d1117", strokeWidth: 2 }}
                    />
                    <Area
                      type="monotone"
                      dataKey="uscite"
                      name="Uscite"
                      stroke="#f87171"
                      strokeWidth={2}
                      fill="url(#gradUscite)"
                      dot={false}
                      activeDot={{ r: 4, fill: "#f87171", stroke: "#0d1117", strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-[200px] text-center">
                  <Banknote className="w-10 h-10 mb-3 text-[hsl(var(--text-mute))]" strokeWidth={1.3} />
                  <p className="text-sm text-[hsl(var(--text-mute))]">Nessun dato disponibile</p>
                </div>
              )}

              {/* Legenda custom */}
              {chartData.length > 0 && (
                <div className="flex items-center gap-5 mt-4 pt-4 border-t border-[hsl(var(--border-soft))]">
                  <span className="inline-flex items-center gap-2 text-xs text-[hsl(var(--text-soft))]">
                    <CircleDot className="w-3.5 h-3.5 text-emerald-400" /> Entrate
                  </span>
                  <span className="inline-flex items-center gap-2 text-xs text-[hsl(var(--text-soft))]">
                    <CircleDot className="w-3.5 h-3.5 text-red-400" /> Uscite
                  </span>
                </div>
              )}
            </div>

            {/* Movimenti — 2 colonne */}
            <div
              className="lg:col-span-2 rounded-3xl border border-[hsl(var(--border-soft))] bg-gradient-to-b from-[hsl(var(--bg-2)/0.85)] to-[hsl(var(--bg-1)/0.95)] p-6 flex flex-col"
              data-testid="movimenti-card"
            >
              <div className="flex items-center gap-4 mb-5">
                <span className="inline-flex w-11 h-11 rounded-2xl items-center justify-center bg-violet-500/12 border border-violet-500/25">
                  <ArrowLeftRight className="w-5 h-5 text-violet-300" />
                </span>
                <div>
                  <h3 className="text-lg font-semibold text-white leading-tight">Movimenti</h3>
                  <p className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--text-mute))] mt-1">
                    Storico transazioni
                  </p>
                </div>
              </div>

              {transactions.length > 0 ? (
                <>
                  <div className="flex-1 divide-y divide-[hsl(var(--border-soft)/0.6)]">
                    {paginatedTransactions.map((t, i) => (
                      <div
                        key={`${page}-${i}`}
                        className="flex items-center gap-3 py-3 hover:bg-[hsl(var(--bg-3)/0.35)] rounded-xl px-2 -mx-2 transition-colors"
                      >
                        <div className="text-[11px] text-[hsl(var(--text-mute))] min-w-[64px] shrink-0">
                          <div className="font-medium text-[hsl(var(--text-soft))]">{t.data}</div>
                          <div>{t.ora}</div>
                        </div>
                        <TransactionIcon tipo={t.tipo} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-white truncate">{t.descrizione}</div>
                        </div>
                        <div className={`text-sm font-bold whitespace-nowrap shrink-0 ${t.importo >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                          {t.importo >= 0 ? "+" : ""}{Number(t.importo).toLocaleString("it-IT", { minimumFractionDigits: 2 })} €
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Paginazione */}
                  {totalPages > 1 && (
                    <div className="mt-4 pt-4 border-t border-[hsl(var(--border-soft))] flex items-center justify-center gap-1.5">
                      <button
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-[hsl(var(--bg-3))] border border-[hsl(var(--border-soft))] text-[hsl(var(--text-soft))] hover:text-white hover:border-[hsl(var(--brand-500))] transition disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                        <button
                          key={p}
                          onClick={() => setPage(p)}
                          className={`inline-flex items-center justify-center w-8 h-8 rounded-xl text-sm font-semibold transition ${
                            p === page
                              ? "bg-[hsl(var(--brand-500))] text-white shadow-[0_4px_14px_-2px_hsl(var(--brand-500)/0.6)]"
                              : "bg-[hsl(var(--bg-3))] border border-[hsl(var(--border-soft))] text-[hsl(var(--text-soft))] hover:text-white hover:border-[hsl(var(--brand-500))]"
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                      <button
                        onClick={() => setPage(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-[hsl(var(--bg-3))] border border-[hsl(var(--border-soft))] text-[hsl(var(--text-soft))] hover:text-white hover:border-[hsl(var(--brand-500))] transition disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
                  <ArrowLeftRight className="w-10 h-10 mb-3 text-[hsl(var(--text-mute))]" strokeWidth={1.3} />
                  <p className="text-sm text-[hsl(var(--text-mute))]">Nessun movimento registrato</p>
                </div>
              )}
            </div>
          </div>

        </div>
      )}
    </section>
  );
}
